import { Show, createMemo, createSignal, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Badge } from '~ui/badge';
import { Button } from '~ui/button';
import { Input } from '~ui/input';
import { Select } from '~ui/select';
import { NumberBox } from '~ui/field';
import { Table, type Column } from '~ui/table';
import { ArrowLeftIcon, CrossIcon, PlusIcon } from '~ui/icons';
import { formatCurrency, formatDay, formatNumber, todayISO } from '~/lib/format';
import { COVERED, price } from '~data';
import { accountRowId, accounts, loanRowId, loans } from '~/stores/portfolio-store';
import type { Loan } from '~/data/portfolios';
import { KIND_LABEL, addTrade, downloadCsv, error, loading, removeTransaction, setKind, transactionTotal, transactions, updateTransaction, type Transaction, type TransactionKind } from '~/stores/transactions-store';
import { disposals, positionAt, positionsIn } from '~/stores/positions';

const accountLabel = (id: string): string => accounts().find((a) => a.id === id)?.label ?? id;

const KINDS: { value: TransactionKind; label: string }[] = [
    { value: 'buy', label: 'Buy' },
    { value: 'sell', label: 'Sell' },
];

/** Sells are marked; buys are not. A blotter is mostly buys, so colouring both
    would be colouring everything — the exception is the thing worth seeing. */
const KindBadge: Component<{ kind: TransactionKind }> = (props) => (
    <Badge interactive={false} class={props.kind === 'sell' ? 'bg-red/15 text-red' : ''}>
        {KIND_LABEL[props.kind]}
    </Badge>
);

/** What a sale actually realised, for the row that made it. Looked up rather
    than recomputed: the cost it relieved depends on the average cost standing at
    that moment, which only the walk knows. */
const disposalFor = (id: string) => disposals().find((d) => d.transaction.id === id) ?? null;

/**
 * Whether this loan could have paid for a buy in this account.
 *
 * A margin loan is secured against the securities in the margin account itself,
 * so it cannot reach into a registered one — the broker has no claim on assets
 * inside a TFSA or an RRSP and will not lend against them. A HELOC is borrowed
 * against your house and can fund anything.
 *
 * Enforced here rather than left to the user, because the arrangement it would
 * describe does not exist: an offered option nobody can actually do is worse
 * than a missing one.
 */
const canFund = (loan: Loan, accountId: string): boolean => {
    if (loan.kind !== 'margin') return true;
    const account = accounts().find((a) => a.id === accountId);
    return account == null || !account.sheltered;
};

// What identifies a trade at a glance, plus what it came to. Fee and the loan
// that funded it stay in the row's own form — they are what you open a row to
// find out, not what you scan a blotter for.
const COLUMNS: Column<Transaction>[] = [
    { id: 'date', header: 'Date', size: 1.3, cell: (t) => formatDay(t().date) },
    { id: 'kind', header: 'Type', size: 0.75, cell: (t) => <KindBadge kind={t().kind} /> },
    { id: 'account', header: 'Account', size: 0.85, cell: (t) => <Text class='truncate'>{accountLabel(t().accountId)}</Text> },
    { id: 'symbol', header: 'Symbol', size: 0.6, cell: (t) => <Text weight='medium'>{t().symbol}</Text> },
    { id: 'units', header: 'Units', align: 'right', size: 0.8, cell: (t) => formatNumber(t().units, { maximumFractionDigits: 4 }) },
    { id: 'price', header: 'Price', align: 'right', size: 0.8, cell: (t) => formatCurrency(t().price) },
    // Money out on a buy, money in on a sell — the figure a blotter is usually
    // being scanned for. Unsigned, with the direction carried by the Type badge
    // beside it rather than by a minus that would read as a loss.
    { id: 'total', header: 'Total', align: 'right', size: 1, cell: (t) => <Text weight='medium'>{formatCurrency(transactionTotal(t()))}</Text> },
];

const NewTradeRow: Component<{ accountId?: string; onBack?: () => void }> = (props) => {
    const [kind, setTradeKind] = createSignal<TransactionKind>('buy');
    const [date, setDate] = createSignal(todayISO());
    const [accountChoice, setAccountChoice] = createSignal('');
    const [symbolChoice, setSymbolChoice] = createSignal(COVERED[0].symbol);
    const [units, setUnits] = createSignal(0);
    const [fee, setFee] = createSignal(4.95);
    // null means "follow the instrument's price"; typing pins it until the
    // symbol changes, so switching tickers does not silently keep a stale price.
    const [priceOverride, setPriceOverride] = createSignal<number | null>(null);

    // Scoped to one account, that account is the answer and the picker is not
    // asked. Otherwise the chosen one can go stale when the active portfolio
    // changes, so fall back to the first available rather than rendering a
    // dangling id.
    const accountId = createMemo(() => {
        if (props.accountId) return props.accountId;
        const list = accounts();
        const chosen = accountChoice();
        return list.some((a) => a.id === chosen) ? chosen : (list[0]?.id ?? '');
    });

    /**
     * What can be traded here.
     *
     * A sale offers what the account actually holds, because selling what you
     * never bought is a typo far more often than it is a missing buy — and when
     * it really is a missing buy, the whole pool is still there to pick from
     * rather than the entry being blocked.
     */
    const tradeable = createMemo(() => {
        if (kind() === 'buy') return COVERED.map((f) => f.symbol);
        const held = positionsIn(accountId()).map((p) => p.symbol);
        return held.length > 0 ? held : COVERED.map((f) => f.symbol);
    });
    const symbol = createMemo(() => (tradeable().includes(symbolChoice()) ? symbolChoice() : (tradeable()[0] ?? '')));

    /** Units on hand, which is what a sale is measured against. */
    const held = createMemo(() => (kind() === 'sell' ? (positionAt(accountId(), symbol())?.units ?? 0) : null));
    // Not blocking. The walk clamps an over-sell and reports the overage rather
    // than letting a position go short, and someone entering a sale they have no
    // buy for is recording something that did happen — flagged, not refused.
    const oversold = createMemo(() => held() != null && units() > held()!);

    // Prefills from the last close when there is one; otherwise the price has to
    // be typed, which is the honest default with no price history.
    const unitPrice = createMemo(() => {
        return priceOverride() ?? (price(symbol()) ?? 0);
    });
    const total = createMemo(() => (kind() === 'sell' ? units() * unitPrice() - fee() : units() * unitPrice() + fee()));
    const valid = createMemo(() => units() > 0 && unitPrice() > 0 && accountId() !== '' && symbol() !== '');

    // Empty means cash. Loans are only offered once one has a balance — an
    // untouched HELOC at zero is not a thing anyone paid with, and listing it
    // would make the common case (cash) one of three equal-looking options.
    //
    // Never on a sale: a disposal is borrowed money coming back, not going out,
    // and the database rejects a sell that names a loan.
    const [loanChoice, setLoanChoice] = createSignal('');
    const funded = createMemo(() => (kind() === 'sell' ? [] : loans().filter((l) => l.balance > 0 && canFund(l, accountId()))));
    // Falls back to cash if the chosen loan is no longer on offer — switching
    // the account to a registered one has to take a margin loan with it rather
    // than leaving a selection the list no longer contains.
    const loanId = createMemo(() => {
        const chosen = loanChoice();
        return funded().some((l) => l.id === chosen) ? chosen : '';
    });

    const [saving, setSaving] = createSignal(false);

    // Only clears the form once the row is actually persisted — a failed save
    // leaves the entry on screen to retry rather than silently discarding it.
    const submit = async () => {
        if (!valid() || saving()) return;
        setSaving(true);
        // The first buy into an account is what creates it: nothing in a book is
        // stored until something is written into it. Resolves to the same id it
        // was given once the row exists, which is every buy after the first.
        const account = await accountRowId(accountId());
        // Same for the loan: naming it on a trade is the first write into it.
        const loan = loanId() === '' ? null : await loanRowId(loanId());
        const ok = await addTrade({ kind: kind(), date: date(), accountId: account, symbol: symbol(), units: units(), price: unitPrice(), fee: fee(), loanId: loan });
        setSaving(false);
        if (!ok) return;
        setUnits(0);
        setPriceOverride(null);
    };

    // Transposed: one field per row, label left and control right.
    //
    // It was a segmented bar of seven controls, which fits a full-width pane and
    // does not fit the quarter-width panel this now lives in — the numeric
    // fields collapsed to a few characters each and the labels inside them ran
    // out of room before the numbers did. Down the page each field gets the
    // panel's whole width and a label that is not competing with its value.
    const Field: Component<{ label: string; children: JSX.Element }> = (fieldProps) => (
        <div class='flex h-row items-stretch justify-between gap-2 border-b pl-2'>
            <div class='flex min-w-0 items-center'>
                <Text tone='muted' class='truncate'>
                    {fieldProps.label}
                </Text>
            </div>
            <div class='flex shrink-0 items-stretch'>{fieldProps.children}</div>
        </div>
    );

    return (
        <div class='shrink-0 border-b' onKeyDown={(e) => e.key === 'Enter' && void submit()}>
            <Show when={props.onBack}>
                <div class='flex h-row items-center gap-2 border-b bg-muted px-1'>
                    <Button size='fill' icon={<ArrowLeftIcon />} label='All transactions' title='Back to the blotter' onClick={props.onBack} />
                </div>
            </Show>
            {/* First, because it changes what the rest of the form means: the
                price is a cost or a proceed, the fund list is the pool or your
                own holdings, and the loan question stops being asked. */}
            <Field label='Type'>
                <Select
                    class='w-36 border-l'
                    options={KINDS}
                    value={kind()}
                    onChange={(e) => {
                        setTradeKind(e.currentTarget.value as TransactionKind);
                        // A price pinned as what you paid is not what you sold
                        // for. Released back to the last close, same as when the
                        // fund changes.
                        setPriceOverride(null);
                    }}
                />
            </Field>

            <Field label='Date'>
                <div class='flex w-36 items-center border-l px-2'>
                    <Input bare type='date' step={undefined} value={date()} onInput={(e) => setDate(e.currentTarget.value)} />
                </div>
            </Field>

            <Show when={!props.accountId}>
                <Field label='Account'>
                    <Select class='w-36 border-l' options={accounts().map((a) => ({ value: a.id, label: a.label }))} value={accountId()} onChange={(e) => setAccountChoice(e.currentTarget.value)} placeholder='Account' />
                </Field>
            </Show>

            <Field label='Fund'>
                <Select
                    class='w-36 border-l'
                    options={tradeable().map((s) => ({ value: s, label: s }))}
                    value={symbol()}
                    onChange={(e) => {
                        setSymbolChoice(e.currentTarget.value);
                        setPriceOverride(null);
                    }}
                />
            </Field>

            {/* The label carries what is on hand, so the one number a sale has
                to be checked against is beside the box you type it into rather
                than on a screen behind this one. */}
            <Field label={held() == null ? 'Units' : `Units · ${formatNumber(held()!, { maximumFractionDigits: 4 })} held`}>
                <NumberBox value={units()} onValue={setUnits} width='w-36' border='l' />
            </Field>
            <Show when={oversold()}>
                <div class='flex h-row items-center border-b px-2'>
                    <Text tone='red'>More than is held — the overage will not be counted</Text>
                </div>
            </Show>
            <Field label='Price'>
                <NumberBox value={unitPrice()} onValue={setPriceOverride} prefix='$' width='w-36' border='l' />
            </Field>
            <Field label='Fee'>
                <NumberBox value={fee()} onValue={setFee} prefix='$' width='w-36' border='l' />
            </Field>

            {/* Only once there is something to choose between. With no loans the
                answer is cash and there is no question to put on screen. */}
            <Show when={funded().length > 0}>
                <Field label='Paid with'>
                    <Select
                        class='w-36 border-l'
                        options={[{ value: '', label: 'Cash' }, ...funded().map((l) => ({ value: l.id, label: l.label }))]}
                        value={loanId()}
                        onChange={(e) => setLoanChoice(e.currentTarget.value)}
                    />
                </Field>
            </Show>

            <div class='flex h-row items-stretch justify-between gap-2 pl-2'>
                <div class='flex min-w-0 items-center'>
                    <Text weight='medium'>{formatCurrency(total())}</Text>
                </div>
                <Button
                    size='fill'
                    icon={<PlusIcon />}
                    label={saving() ? 'Saving' : 'Add'}
                    disabled={!valid() || saving()}
                    onClick={() => void submit()}
                    title={kind() === 'sell' ? 'Record sale' : 'Record buy'}
                    class='w-36 shrink-0 border-l'
                />
            </div>
        </div>
    );
};

/** One field of the detail form: label left, value right, same shape as the
    entry form so a row and a new row read the same way round. */
const Detail: Component<{ label: string; children: JSX.Element; hint?: string }> = (props) => (
    <div class='flex h-row items-stretch justify-between gap-2 border-b pl-2 whitespace-nowrap' title={props.hint}>
        <div class='flex min-w-0 items-center'>
            <Text tone='muted' class='truncate'>
                {props.label}
            </Text>
        </div>
        <div class='flex shrink-0 items-stretch'>{props.children}</div>
    </div>
);

/**
 * One trade, in full — and editable.
 *
 * The same fields the entry form has, in the same order, because correcting a
 * trade is the same act as recording one with the values already filled in. A
 * separate read-only view would have meant two layouts of one thing and a
 * round trip through delete-and-retype to fix a typo'd fee.
 *
 * Saved per field on a debounce rather than behind a Save button: there is no
 * draft state to abandon, so a button would only be a thing to forget to press.
 */
const TransactionDetail: Component<{ transaction: Transaction; onBack: () => void }> = (props) => {
    const [removing, setRemoving] = createSignal(false);
    const isSell = () => props.transaction.kind === 'sell';
    const funded = createMemo(() => (isSell() ? [] : loans().filter((l) => (l.balance > 0 || l.id === props.transaction.loanId) && canFund(l, props.transaction.accountId))));
    const sale = createMemo(() => (isSell() ? disposalFor(props.transaction.id) : null));
    const signed = (v: number): 'green' | 'red' => (v >= 0 ? 'green' : 'red');

    return (
        <>
            <div class='flex h-row shrink-0 items-center gap-2 border-b bg-muted px-1'>
                <Button size='fill' icon={<ArrowLeftIcon />} label='All transactions' title='Back to the blotter' onClick={props.onBack} />
            </div>

            {/* Editable, because the commonest way this is wrong is the direction
                — and turning a buy into a sell also drops the loan it named, in
                one write, since a sale may not carry one. */}
            <Detail label='Type'>
                <Select class='w-36 border-l' options={KINDS} value={props.transaction.kind} onChange={(e) => setKind(props.transaction.id, e.currentTarget.value as TransactionKind)} />
            </Detail>

            <Detail label='Date'>
                <div class='flex w-36 items-center border-l px-2'>
                    <Input bare type='date' step={undefined} value={props.transaction.date} onInput={(e) => updateTransaction(props.transaction.id, 'date', e.currentTarget.value)} />
                </div>
            </Detail>

            <Detail label='Account'>
                <Select
                    class='w-36 border-l'
                    options={accounts().map((a) => ({ value: a.id, label: a.label }))}
                    value={props.transaction.accountId}
                    onChange={(e) => {
                        const next = e.currentTarget.value;
                        updateTransaction(props.transaction.id, 'accountId', next);
                        // A margin loan cannot follow a buy into a TFSA — see
                        // canFund. Cleared rather than left to sit there
                        // describing an impossible arrangement.
                        const loan = loans().find((l) => l.id === props.transaction.loanId);
                        if (loan && !canFund(loan, next)) updateTransaction(props.transaction.id, 'loanId', null);
                    }}
                />
            </Detail>

            <Detail label='Fund'>
                <Select
                    class='w-36 border-l'
                    options={COVERED.map((f) => ({ value: f.symbol, label: f.symbol }))}
                    value={props.transaction.symbol}
                    onChange={(e) => updateTransaction(props.transaction.id, 'symbol', e.currentTarget.value)}
                />
            </Detail>

            <Detail label='Units'>
                <NumberBox value={props.transaction.units} onValue={(n) => updateTransaction(props.transaction.id, 'units', n)} width='w-36' border='l' />
            </Detail>
            <Detail label='Price'>
                <NumberBox value={props.transaction.price} onValue={(n) => updateTransaction(props.transaction.id, 'price', n)} prefix='$' width='w-36' border='l' />
            </Detail>
            <Detail label='Fee'>
                <NumberBox value={props.transaction.fee} onValue={(n) => updateTransaction(props.transaction.id, 'fee', n)} prefix='$' width='w-36' border='l' />
            </Detail>

            {/* Derived, so it stays a figure rather than a field: changing the
                total would have to decide which of the three inputs gave. */}
            <Detail label={isSell() ? 'Proceeds' : 'Total'}>
                <Text weight='medium' class='px-2'>
                    {formatCurrency(transactionTotal(props.transaction))}
                </Text>
            </Detail>

            {/* What the sale actually made, which is not on the trade itself:
                the cost it relieved is the average cost standing at the moment
                it happened, so it comes from the walk over the whole blotter.

                Against book cost, not the return-of-capital-adjusted base. RoC
                lowers the real cost base and so raises the real gain — by a lot,
                for this pool — and the position panel carries that figure; a
                trade row is the wrong place to restate it, because the answer
                belongs to the position rather than to this one sale. */}
            <Show when={sale()}>
                <Detail label='Cost relieved' hint='The average cost of the units sold, at the moment of the sale'>
                    <Text class='px-2'>{formatCurrency(sale()!.relieved)}</Text>
                </Detail>
                <Detail label='Realised gain' hint='Proceeds less the cost base the sale relieved, before return of capital'>
                    <Text tone={signed(sale()!.gain)} weight='medium' class='px-2'>
                        {formatCurrency(sale()!.gain)}
                    </Text>
                </Detail>
                <Show when={sale()!.unmatched > 0}>
                    <Detail label='Not held'>
                        <Text tone='red' class='px-2'>
                            {formatNumber(sale()!.unmatched, { maximumFractionDigits: 4 })} units
                        </Text>
                    </Detail>
                </Show>
            </Show>

            {/* This one field is the whole record of where borrowed money went —
                every allocation and deductibility figure on the Loans page is
                derived from it, which is why it is editable here rather than
                being something you delete and re-enter a trade to change.

                Not asked on a sale. The proceeds land as cash whatever paid for
                the units; what the loan had in them is released pro rata by the
                walk, and the balance still owed is edited on the loan. */}
            <Show when={!isSell()}>
                <Detail label='Paid with'>
                    <Select
                        class='w-36 border-l'
                        options={[{ value: '', label: 'Cash' }, ...funded().map((l) => ({ value: l.id, label: l.label }))]}
                        value={props.transaction.loanId ?? ''}
                        onChange={(e) => updateTransaction(props.transaction.id, 'loanId', e.currentTarget.value || null)}
                    />
                </Detail>
            </Show>

            <div class='flex h-row shrink-0 items-center border-t px-1'>
                {/* Asks once rather than firing on a single click: a removed
                    trade takes its units and its loan attribution with it, and
                    every average cost after it in the blotter moves. */}
                <Button
                    size='fill'
                    class={`w-full ${removing() ? 'bg-red text-background hover:bg-red!' : ''}`}
                    label={removing() ? 'Remove for good?' : 'Remove'}
                    title='Delete this trade'
                    onClick={() => {
                        if (!removing()) {
                            setRemoving(true);
                            return;
                        }
                        void removeTransaction(props.transaction.id);
                        props.onBack();
                    }}
                />
            </div>
        </>
    );
};

const Transactions: Component<{ accountId?: string; symbol?: string; loanId?: string; onClose?: () => void }> = (props) => {
    // The blotter holds every portfolio's buys, so scope it to the one on screen
    // — otherwise another portfolio's rows show up here, with the raw account id
    // in the Account column because no label resolves for them. Given one
    // account, narrow to it: an unfiltered blotter beside a single account's
    // holdings invites reading another account's rows as that account's.
    const accountIds = createMemo(() => new Set(accounts().map((a) => a.id)));
    const visible = createMemo(() =>
        transactions.filter((t) => (props.accountId ? t.accountId === props.accountId : accountIds().has(t.accountId))).filter((t) => (props.symbol ? t.symbol === props.symbol : true))
            // Buys this loan paid for. The blotter is where that is recorded —
            // a trade names the loan that funded it — so this is a filter rather
            // than a second set of rows kept alongside.
            .filter((t) => (props.loanId ? t.loanId === props.loanId : true)),
    );
    // What the panel is showing: the list, one transaction, or the entry form.
    const [open, setOpen] = createSignal<Transaction | 'new' | null>(null);

    const label = () => accounts().find((a) => a.id === props.accountId)?.label;
    const bought = createMemo(() => visible().reduce((sum, t) => (t.kind === 'buy' ? sum + transactionTotal(t) : sum), 0));
    const sold = createMemo(() => visible().reduce((sum, t) => (t.kind === 'sell' ? sum + transactionTotal(t) : sum), 0));
    const fees = createMemo(() => visible().reduce((sum, t) => sum + t.fee, 0));

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection>
                    <Heading level={2} class='pl-2'>
                        {label() ?? 'Transactions'}
                    </Heading>
                </ToolbarSection>
                <ToolbarSection>
                    <Show when={error()}>
                        <Text tone='red' class='whitespace-nowrap pr-2'>
                            {error()}
                        </Text>
                    </Show>
                    {/* Sold is only shown once something has been: on a blotter
                        that is all buys — most of them — a permanent "$0 sold"
                        is a column of nothing. */}
                    <Text tone='muted' class='whitespace-nowrap pr-2'>
                        {visible().length} trades · {formatCurrency(bought())} bought{sold() > 0 ? ` · ${formatCurrency(sold())} sold` : ''} · {formatCurrency(fees())} fees
                    </Text>
                    <Button size='fill' icon={<PlusIcon />} label='New' title='Record a trade' class={open() === 'new' ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''} onClick={() => setOpen((current) => (current === 'new' ? null : 'new'))} />
                    <Button size='fill' label='CSV' title='Download as CSV' onClick={() => downloadCsv(visible())} />
                    <Show when={props.onClose}>
                        <Button size='fill' icon={<CrossIcon />} title='Hide transactions' onClick={props.onClose} />
                    </Show>
                </ToolbarSection>
            </Toolbar>

            {/* Three states, one panel: the blotter, one row in full, or the
                entry form. Only one is ever a useful thing to be looking at, and
                a quarter-width panel holding all three at once was the entry row
                squeezing the list it was adding to. */}
            <Show when={open()} fallback={<Table columns={COLUMNS} rows={visible()} empty={loading() ? 'Loading…' : 'No trades recorded'} onRowClick={(t) => setOpen(t)} />}>
                <Show when={open() === 'new'} fallback={<TransactionDetail transaction={open() as Transaction} onBack={() => setOpen(null)} />}>
                    <NewTradeRow accountId={props.accountId} onBack={() => setOpen(null)} />
                </Show>
            </Show>
        </Panel>
    );
};

export default Transactions;
